import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { atLeast, ROLE_HOME } from '@/lib/rbac';
import type { Role } from '@/types/domain';

/** Brief hold while the real session resolves — prevents bouncing a logged-in
 *  user to /welcome on refresh. */
function AuthSplash() {
  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--paper)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--sage)', opacity: 0.9 }} />
    </div>
  );
}

/**
 * Route guard. Renders children only when the signed-in user's role ranks at or
 * above `min`; a user who hasn't finished onboarding is sent to complete it.
 * This is UX-level protection — the real boundary is Supabase RLS.
 */
export function RequireRole({ min, children }: { min: Role; children: ReactNode }) {
  const { user, role, loading } = useAuth();
  const { search } = useLocation();
  if (loading) return <AuthSplash />;
  if (!user) return <Navigate to={`/welcome${search}`} replace />;
  if (user.onboarded === false) return <Navigate to={`/welcome${search}`} replace />;
  if (!atLeast(role, min)) return <Navigate to={role ? ROLE_HOME[role] : '/welcome'} replace />;
  return <>{children}</>;
}

/** Redirects "/" to the right home section for the current role. */
export function RoleHomeRedirect() {
  const { user, role, loading } = useAuth();
  const { search } = useLocation();
  if (loading) return <AuthSplash />;
  if (!user || !role) return <Navigate to={`/welcome${search}`} replace />;
  if (user.onboarded === false) return <Navigate to={`/welcome${search}`} replace />;
  // A scanned venue QR (/?checkin=token&course=id) → straight to attendance check-in.
  const p = new URLSearchParams(search);
  const checkin = p.get('checkin');
  const course = p.get('course');
  if (checkin && course) return <Navigate to={`/learn/course/${course}/attendance?checkin=${checkin}`} replace />;
  return <Navigate to={ROLE_HOME[role]} replace />;
}
